import React, { useMemo, useState } from 'react';
import { useMap, Map as Gmap, InfoWindow } from '@vis.gl/react-google-maps';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../../constants';
import { matchWeatherWithETAs, formatTime } from '../../services/google';
import Marker from './Marker';
import Polyline, { InfoWindow as PolylineInfoWindow } from './Polyline';
import MapHandler from './MapHandler';

const Map = ({ origin, destinations, timeZone, routes }) => {
  const map = useMap();
  const [infoWindow, setInfoWindow] = useState(null);
  const [activeRoute, setActiveRoute] = useState(0);

  const stops = useMemo(() => {
    const stopsArray = [];
    const weatherData = matchWeatherWithETAs([origin, ...destinations], routes[activeRoute], timeZone);

    if (origin?.position) stopsArray.push({ ...origin, type: 'origin', weatherData: weatherData[0] });
    destinations.forEach((dest, index) => {
      if (dest?.position) {
        stopsArray.push({ ...dest, type: 'destination', index, weatherData: weatherData[index + 1] });
      }
    });
    return stopsArray;
  }, [origin, destinations, routes, activeRoute]);

  const handlePolylineClick = (event, route, routeIndex) => {
    const position = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };
    setActiveRoute(routeIndex);
    setInfoWindow({
      position,
      content: <PolylineInfoWindow route={route} />,
    });
  };

  const renderRoutes = useMemo(() => {
    if (!routes || routes.length === 0) return null;

    return routes.map((route, routeIndex) => {
      // Each route may have multiple legs when there are waypoints
      const legs = route.legs || [];

      return legs.map((leg, legIndex) => (
        <Polyline
          key={`polyline-${routeIndex}-${legIndex}`}
          route={{
            ...route,
            polyline: leg.polyline,
            travelAdvisory: leg.travelAdvisory,
            duration: leg.duration,
            distanceMeters: leg.distanceMeters,
          }}
          activeRoute={activeRoute}
          routeIndex={routeIndex}
          onClick={handlePolylineClick}
        />
      ));
    });
  }, [routes, activeRoute]);

  return (
    <Gmap
      mapId={process.env.REACT_APP_GOOGLE_MAP_ID}
      defaultZoom={DEFAULT_MAP_ZOOM}
      defaultCenter={DEFAULT_MAP_CENTER}
      gestureHandling="greedy"
      disableDefaultUI={true}
    >
      <MapHandler map={map} stops={stops} />
      {stops.map((stop, index) => (
        <Marker
          key={`marker-${index}`}
          type={'StopMarker'}
          position={stop.position}
          weatherData={
            stop.weatherData
              ? {
                  time: formatTime(stop.weatherData.eta),
                  degree: stop.weatherData.degree,
                  iconUrl: stop.weatherData.iconUrl,
                  dayDiff: stop.weatherData.dayDiff,
                }
              : null
          }
        />
      ))}
      {renderRoutes}
      {infoWindow && (
        <InfoWindow
          key={infoWindow.position.lat + ',' + infoWindow.position.lng}
          position={infoWindow.position}
          onCloseClick={() => setInfoWindow(null)}
        >
          {infoWindow.content}
        </InfoWindow>
      )}
    </Gmap>
  );
};

export default React.memo(Map);
